import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../Icon";
import { useMemo, useState } from "react";
import type { Compendium } from "@lancer-vtt/compendium";
import type { EncounterCommand, NpcWear, PilotData, Side } from "@lancer-vtt/domain";
import type { NpcClassBlock, NpcTier, LoadedOverlay } from "@lancer-vtt/rules";
import { resolveMechArtUrl } from "../../mechArt";
import { mechDeployRows, mechDeployHoldReason, npcDeployOptions, npcTemplateOptions, npcOptionalSections, countPicksIn, templateProhibitReason, deployMech, deployNpc, deployPilot, pilotDeployRows, concealedDeploy, type MechDeployRow, type NpcDeployOption, type NpcTemplateOption, type PilotDeployRow } from "../deploy";
import { nameGlyph } from "../../../routes/home/combatCells";
import { AvatarImage } from "../../../routes/home/PilotAvatar";
import { assignmentStrandsUnit } from "../controlAssignment";
import type { ControlPanelSlot } from "./ControlPanel";

/** NPC tiers a class can deploy at (Lancer tiers 1–3). */
const NPC_TIERS: NpcTier[] = [1, 2, 3];

/** Which source the transient DEPLOY picker offers (UNITS' ADD ENEMY → npc, ADD MECH → mech, ADD PILOT → pilot). */
export type PickerKind = "npc" | "mech" | "pilot";

/**
 * The DEPLOY picker window body — the transient singleton the UNITS
 * window opens to add a combatant, in the lighter "＋ add" weight (not a full GMS surface). Three
 * sources behind one window, selected by {@link PickerKind}:
 *
 * - **npc** — every loaded NPC class (`compendium.list("npc_class")`, served via the world-packs
 * channel) with a tier selector (1/2/3); a graceful absence state when no pack is loaded.
 * - **mech** — one row per roster pilot-mech (`mechDeployRows`); a frameless mech's deploy is
 * disabled (mirrors `spawnMech`'s rejection).
 * - **pilot** — one row per roster pilot, deployed on foot with no mech (`pilotDeployRows` /
 * `deployPilot`) — a GM-convenience path alongside the
 * Dismount/Eject bridge, which spawns the pilot combatant server-side without going through this
 * picker at all. Always deployable — `spawnPilot` has no frame-resolution dependency to reject on.
 *
 * A pick runs the shared `deployNpc`/`deployMech`/`deployPilot` (real spawn adapters → `add_combatant`), dispatches
 * the command (the unit lands UNPLACED — the select-then-click-empty-cell flow places it),
 * **auto-selects** the just-deployed unit via `onDeployed` (so the next map click drops it — no
 * manual roster-row click), and the window **self-closes** (`onClose`). A spawn rejection surfaces
 * inline and keeps the window open.
 */
export function DeployPickerBody({
  kind,
  compendium,
  overlay,
  loadingOverlay,
  overlayError = null,
  pilots,
  rosterLoading,
  actorId,
  disabled,
  canConceal,
  ownerSlots,
  npcWear,
  onClearNpcWear,
  dispatch,
  onDeployed,
  onClose,
}: {
  kind: PickerKind;
  compendium: Compendium;
  /**
   * The merged overlay, for the per-row "fiction-only content" sub-tag.
   * **Optional, and its absence means "say nothing"**: a host whose overlay is still loading passes
   * `undefined` and no row is tagged, rather
   * than passing a partial overlay that would tag nearly every row. This component takes the
   * compendium as a prop rather than reading context, so the overlay follows the same shape.
   */
  overlay?: LoadedOverlay;
  /**
   * True until {@link overlay}'s sidecar fetch has settled (`CompendiumState.loadingOverlay`) —
   * the separate flag that tells `undefined` "still loading" APART from `undefined` "the host has
   * none". The sub-tag above cannot tell the two apart and does not need to; the mech deploy
   * does, because it hands the overlay to `spawnMech` and the caps that spawn freezes are
   * permanent. Held here via {@link mechDeployHoldReason} — see its doc for why this one is a
   * block rather than another advisory sub-tag. Mirrors `MechInspectorBody`'s own
   * `overlay` + `loadingOverlay` prop pair.
   */
  loadingOverlay: boolean;
  /** Why the overlay failed to load (`CompendiumState.overlayError`); keeps the mech deploy held, named. */
  overlayError?: string | null;
  pilots: PilotData[];
  rosterLoading: boolean;
  actorId: string;
  disabled: boolean;
  /**
   * GM-only: offer the "deploy concealed" option. When ticked, a pick
   * dispatches the atomic `batch[add_combatant, set_concealed]` ({@link concealedDeploy}) — one
   * apply, one projected broadcast, so the unit never flashes on a player client between the add
   * and the conceal. A player viewer never sees the option (`set_concealed` is GM-only at the gate).
   */
  canConceal: boolean;
  /**
   * GM-only: the game's slots, for the NPC section's **OWNER** + **SIDE** rows — an NPC can be born
   * owned by a player slot and/or on the PC side (`unit-control-assignment` Phase E), instead of
   * deploying GM-owned and re-homing it from the CONTROL panel. Omitted ⇒ no rows, and every NPC
   * deploys GM-owned, NPC-side. The vocabulary is the CONTROL panel's OWNER row and its `{NPC}`-side
   * stranding warning (advise, never refuse); the CLI twin is `add --npc --slot <id> | --gm [--side pc|npc]`.
   */
  ownerSlots?: ControlPanelSlot[];
  /**
   * GM-only: the roster's allied-NPC wear ledger (`RosterState.npcWear`, `decisions/0196`). When
   * passed, the NPC config offers **Persistent as**: a key the NPC spawns from (its carried HP /
   * Structure / Stress) and that END COMBAT writes back to. Omitted ⇒ no field, and every NPC is
   * ephemeral. The CLI twin is `add --npc --persistent <key>`.
   */
  npcWear?: Record<string, NpcWear>;
  /**
   * GM-only: clear a key's carried wear (`clear_npc_wear` on the roster), the fiction repairing
   * it. Offered beside the carried wear when the typed key has any. Returns the roster's
   * rejection, if any.
   */
  onClearNpcWear?: (key: string) => string | null;
  dispatch: (command: EncounterCommand) => unknown;
  /**
   * Called with the just-deployed combatant's id on a successful pick, so the host can auto-select it
   * in the UNITS roster — the unit lands unplaced, and selecting it arms first-placement, so the very
   * next map click drops it (eliminating the manual "click the new row to select it" step). The id is
   * the client-minted `combatant.id` (known synchronously, before the server round-trip), so this is
   * never a brittle name-match. Fires for both the plain and the deploy-concealed batch paths.
   */
  onDeployed?: (combatantId: string) => void;
  onClose: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [tier, setTier] = useState<NpcTier>(1);
  const [concealed, setConcealed] = useState(false);
  // The NPC's born owner (`null` ⇒ GM) and side. Reset with the window, like `concealed`.
  const [owner, setOwner] = useState<string | null>(null);
  const [side, setSide] = useState<Side>("npc");
  const npcOptions = useMemo(() => npcDeployOptions(compendium), [compendium]);
  const templateOptions = useMemo(() => npcTemplateOptions(compendium), [compendium]);
  const mechRows = useMemo(() => mechDeployRows(pilots, compendium, overlay), [pilots, compendium, overlay]);
  /** Non-null ⇒ the Mechs section is held; see {@link mechDeployHoldReason}. Bounded — it clears when the fetch settles. */
  const mechHold = mechDeployHoldReason(loadingOverlay, overlayError);
  const pilotRows = useMemo(() => pilotDeployRows(pilots), [pilots]);

  // Staged NPC selection: clicking a class in the list no
  // longer deploys instantly — it STAGES the class (highlighted) and expands the template /
  // optional-feature config panel below it, so the GM can compose a "Veteran Ace with Bombing
  // Bay" before committing. A second click on the SAME class collapses it back; picking a
  // DIFFERENT class re-stages fresh (drops any templateIds/optionalFeatureIds picked for the
  // prior class — those pools are per-class/per-template, a stale id from a different class's
  // pool is inert in `composeNpcFeatures` anyway, but resetting keeps the panel honest).
  const [selectedNpcId, setSelectedNpcId] = useState<string | null>(null);
  const [templateIds, setTemplateIds] = useState<string[]>([]);
  const [optionalFeatureIds, setOptionalFeatureIds] = useState<string[]>([]);
  // The staged NPC's persistent key; blank ⇒ an ordinary ephemeral NPC.
  const [persistentKey, setPersistentKey] = useState("");

  const onToggleNpcSelect = (opt: NpcDeployOption) => {
    if (selectedNpcId === opt.id) {
      setSelectedNpcId(null);
      return;
    }
    setSelectedNpcId(opt.id);
    setTemplateIds([]);
    setOptionalFeatureIds([]);
    setPersistentKey("");
  };
  const onToggleTemplate = (id: string) =>
    setTemplateIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const onToggleOptional = (id: string) =>
    setOptionalFeatureIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const onDeployNpc = (opt: NpcDeployOption) => {
    const key = persistentKey.trim();
    const persistent = npcWear && key !== "" ? { key, wear: npcWear[key] } : undefined;
    const r = deployNpc({ actorId, npcClass: opt.npcClass, tier, compendium, templateIds, optionalFeatureIds, controllerSlotId: owner, allegiance: side, ...(persistent ? { persistent } : {}), ...(overlay ? { overlay } : {}) });
    if (!r.ok) return setError(r.error);
    dispatch(canConceal && concealed ? concealedDeploy(r.value) : r.value);
    onDeployed?.(r.value.combatant.id);
    onClose();
  };
  const onPickMech = (row: MechDeployRow) => {
    // Belt-and-braces beside the disabled button below: the hold is a rules-correctness gate, not
    // a styling one, so it refuses here too rather than trusting `disabled` to be the only path in.
    // `deployPickerOverlayHold.render.test.tsx` covers the `disabled` half (a click on a disabled
    // button reaches no handler, in happy-dom as in a browser); this line is the backstop for an
    // edit that drops `mechHold` from `disabled` while restyling, and is deliberately NOT
    // independently exercised by that file.
    if (mechHold !== null) return setError(mechHold);
    const r = deployMech({ actorId, pilot: row.pilot, mechIdx: row.mechIdx, compendium, ...(overlay ? { overlay } : {}) });
    if (!r.ok) return setError(r.error);
    dispatch(canConceal && concealed ? concealedDeploy(r.value) : r.value);
    onDeployed?.(r.value.combatant.id);
    onClose();
  };
  const onPickPilot = (row: PilotDeployRow) => {
    const r = deployPilot({ actorId, pilot: row.pilot, compendium });
    if (!r.ok) return setError(r.error);
    dispatch(canConceal && concealed ? concealedDeploy(r.value) : r.value);
    onDeployed?.(r.value.combatant.id);
    onClose();
  };

  return (
    <div className="gms-content cw-picker">
      {error && <div className="cw-error">{error}</div>}

      {/* Deploy-concealed — GM-only. Ticked, the next pick lands already
          concealed (atomic batch, no player-visible flash); the checkbox resets with the window. */}
      {canConceal && (
        <label className="cw-deploy-concealed" title="Deploy the picked unit already concealed — players never see it until you reveal it.">
          <input type="checkbox" checked={concealed} disabled={disabled} onChange={(e) => setConcealed(e.target.checked)} />
          <span>◌ deploy concealed</span>
        </label>
      )}

      {kind === "npc" ? (
        npcOptions.length === 0 ? (
          <div className="cw-hint">No NPC pack loaded — add an .lcp to &lt;world&gt;/packs/ to enable enemies.</div>
        ) : (
          <>
            <div className="cw-tierbar" role="group" aria-label="tier">
              <span className="cw-tierbar-label">Tier</span>
              {NPC_TIERS.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`cw-tier${tier === t ? " cw-tier-on" : ""}`}
                  aria-pressed={tier === t}
                  onClick={() => setTier(t)}
                >
                  {t}
                </button>
              ))}
            </div>
            {ownerSlots && (
              <NpcOwnerRows slots={ownerSlots} owner={owner} side={side} disabled={disabled} onOwner={setOwner} onSide={setSide} />
            )}
            <ul className="cw-pick-list">
              {npcOptions.map((opt) => {
                const isSelected = selectedNpcId === opt.id;
                return (
                  <li key={opt.id}>
                    <button
                      type="button"
                      className={`cw-pick${isSelected ? " cw-pick-on" : ""}`}
                      disabled={disabled}
                      title={isSelected ? `Collapse ${opt.name}'s deploy config` : `Configure ${opt.name} (tier ${tier}) before deploying`}
                      onClick={() => onToggleNpcSelect(opt)}
                    >
                      <span className="cw-pick-glyph">{nameGlyph(opt.name)}</span>
                      <span className="cw-pick-name">{opt.name}</span>
                    </button>
                    {isSelected && (
                      <NpcDeployConfig
                        compendium={compendium}
                        npcClass={opt.npcClass}
                        tier={tier}
                        templateOptions={templateOptions}
                        templateIds={templateIds}
                        onToggleTemplate={onToggleTemplate}
                        optionalFeatureIds={optionalFeatureIds}
                        onToggleOptional={onToggleOptional}
                        disabled={disabled}
                        persistent={
                          npcWear
                            ? {
                                value: persistentKey,
                                wear: npcWear,
                                onKey: setPersistentKey,
                                ...(onClearNpcWear ? { onClear: (key: string) => setError(onClearNpcWear(key)) } : {}),
                              }
                            : undefined
                        }
                        onDeploy={() => onDeployNpc(opt)}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )
      ) : rosterLoading ? (
        <div className="cw-hint">Loading roster…</div>
      ) : kind === "mech" ? (
        mechRows.length === 0 ? (
          <div className="cw-hint">No roster mechs. Build a pilot + pick a frame first.</div>
        ) : (
          <>
            {/* The guard's visible half. A silent uncapped mech is the failure this exists to
                prevent, so the reason is on screen, not only in a `title`. */}
            {mechHold !== null && <div className="cw-hint cw-deploy-hold">⏳ {mechHold}</div>}
            <ul className="cw-pick-list">
            {mechRows.map((row) => {
              // mech-portrait 2.5: a small chassis-art thumbnail in the glyph slot when the
              // mech's frame resolves art; falls back to the existing `kindGlyph("mech")` glyph
              // when there's no frame yet OR the art fails to load — never a broken-image icon.
              const artUrl = resolveMechArtUrl(row);
              // An invalid loadout is a
              // PRESENTATION-only flag on this row — the deploy button stays enabled
              // (unlike the genuinely-disabled frameless row above) and clicking it still
              // deploys via `onPickMech` unchanged. The `title` carries every violation
              // message so a GM sees the reason without opening the editor first.
              const invalid = row.violations.length > 0;
              const title = mechHold !== null
                ? mechHold
                : !row.frameId
                ? "Pick a frame on the pilot license first"
                : invalid
                  ? `Deploy this mech (unplaced) — ⚠ invalid loadout: ${row.violations.map((v) => v.message).join("; ")}`
                  : "Deploy this mech (unplaced)";
              return (
                <li key={`${row.pilot.id}:${row.mechId}`}>
                  <button
                    type="button"
                    className="cw-pick"
                    disabled={disabled || !row.frameId || mechHold !== null}
                    title={title}
                    onClick={() => onPickMech(row)}
                  >
                    <span className="cw-pick-glyph">
                      {artUrl ? (
                        <AvatarImage
                          src={artUrl}
                          alt=""
                          className="cw-pick-thumb"
                          fallback={nameGlyph(row.mechName)}
                        />
                      ) : (
                        nameGlyph(row.mechName)
                      )}
                    </span>
                    <span className="cw-pick-name">
                      {row.mechName}
                      <span className="cw-pick-sub">
                        {row.pilot.callsign || "(no callsign)"}
                        {row.frameId ? "" : " · no frame"}
                        {invalid && <span className="cw-pick-invalid"> · <Icon icon={faTriangleExclamation} className="icon-lead" />invalid loadout</span>}
                        {/* The GM-side backstop, and the
                            same non-blocking shape as `cw-pick-invalid` beside it: the button
                            stays enabled and the deploy is unchanged. Empty whenever the
                            overlay hasn't loaded, because `mechRows` passes `undefined` then. */}
                        {row.fictionOnly.length > 0 && (
                          <span className="cw-pick-fiction"> · <Icon icon={faTriangleExclamation} className="icon-lead" />fiction-only content</span>
                        )}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
            </ul>
          </>
        )
      ) : pilotRows.length === 0 ? (
        <div className="cw-hint">No roster pilots. Build a pilot first.</div>
      ) : (
        <ul className="cw-pick-list">
          {pilotRows.map((row) => (
            <li key={row.pilotId}>
              <button
                type="button"
                className="cw-pick"
                disabled={disabled}
                title="Deploy this pilot on foot (unplaced)"
                onClick={() => onPickPilot(row)}
              >
                <span className="cw-pick-glyph">{nameGlyph(row.callsign)}</span>
                <span className="cw-pick-name">
                  {row.callsign}
                  <span className="cw-pick-sub">{row.pilotName}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The NPC section's **OWNER** and **SIDE** rows (`unit-control-assignment` Phase E): who the NPC is
 * born owned by, and which side it activates on. Defaults GM / NPC. A player owner on the NPC side
 * shows the CONTROL panel's stranding warning ({@link assignmentStrandsUnit}) — advice, not a
 * block, since the GM may want exactly that.
 */
function NpcOwnerRows({
  slots,
  owner,
  side,
  disabled,
  onOwner,
  onSide,
}: {
  slots: ControlPanelSlot[];
  owner: string | null;
  side: Side;
  disabled: boolean;
  onOwner: (slotId: string | null) => void;
  onSide: (side: Side) => void;
}) {
  const players = slots.filter((s) => s.role === "player");
  const stranded = assignmentStrandsUnit({ combatant: { base: { allegiance: side } }, slotId: owner });
  return (
    <div className="cw-control-assign">
      <div className="cw-control-row" role="group" aria-label="born owner">
        <span className="cw-microlabel">OWNER</span>
        <button type="button" className={`cw-chip${owner === null ? " cw-chip-on" : ""}`} aria-pressed={owner === null} disabled={disabled} title="Deploy GM-owned" onClick={() => onOwner(null)}>
          GM
        </button>
        {players.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`cw-chip${owner === s.id ? " cw-chip-on" : ""}`}
            aria-pressed={owner === s.id}
            disabled={disabled}
            title={`Deploy owned by ${s.name}`}
            onClick={() => onOwner(s.id)}
          >
            {s.name}
          </button>
        ))}
      </div>
      <div className="cw-control-row" role="group" aria-label="born side">
        <span className="cw-microlabel">SIDE</span>
        {(["npc", "pc"] as const).map((v) => (
          <button
            key={v}
            type="button"
            className={`cw-chip${side === v ? " cw-chip-on" : ""}`}
            aria-pressed={side === v}
            disabled={disabled}
            title={v === "npc" ? "Activates in the hostile (NPC) bucket" : "Activates in the player (PC) bucket"}
            onClick={() => onSide(v)}
          >
            {v.toUpperCase()}
          </button>
        ))}
      </div>
      {stranded && (
        <div className="cw-control-warn">
          <span className="cw-microlabel">
            {"{NPC}"}-side — its owner will still activate it in the HOSTILE bucket. To let a player drive it on the hostile
            turn, deploy it GM-owned and use GRANTS.
          </span>
          <button type="button" className="cw-chip" disabled={disabled} title="Deploy it on the player side" onClick={() => onSide("pc")}>
            MAKE PC-SIDE
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * The DEPLOY picker's per-class **template + optional-feature config panel** — expands under a
 * staged NPC class row (see {@link DeployPickerBody}).
 * Two sections:
 *
 * - **Templates** — a multi-select chip row (the tier bar's toggle-button style). A candidate
 * that would trip `prohibitTemplates` against an already-picked template takes a *dimension-stable*
 * discouraged state (`.cw-tmpl-warn` — greyed + dashed + struck-through, no inserted glyph, so the
 * chip row never reflows) and carries its reason in the `title`
 * tooltip — advisory only ({@link templateProhibitReason}): it stays
 * fully clickable, never `disabled`, so a GM can deploy the "incompatible" combo on purpose.
 * - **Optional features** — one section per currently-applicable source (the class's own list,
 * then each picked template's own list, in pick order — {@link npcOptionalSections}), each with
 * a budget chip ({@link computeNpcOptionalBudgets} via that helper) that recolors when the
 * current pick count falls outside `[min, max]` — still just a chip, never a blocking gate
 * (the CLI's `computeNpcDeployAdvisories` mirrors the same math server-side… well, client-side,
 * but the same non-enforcing spirit).
 *
 * A "Deploy" button commits the staged `templateIds`/`optionalFeatureIds` through
 * {@link deployNpc}. Pure presentation over the pure `deploy.ts` helpers — no composition math
 * lives here, mirroring how the CLI's `add --npc` reuses the SAME exports rather than re-deriving.
 */
function NpcDeployConfig({
  compendium,
  npcClass,
  tier,
  templateOptions,
  templateIds,
  onToggleTemplate,
  optionalFeatureIds,
  onToggleOptional,
  disabled,
  persistent,
  onDeploy,
}: {
  compendium: Compendium;
  npcClass: NpcClassBlock;
  tier: NpcTier;
  templateOptions: NpcTemplateOption[];
  templateIds: string[];
  onToggleTemplate: (id: string) => void;
  optionalFeatureIds: string[];
  onToggleOptional: (id: string) => void;
  disabled: boolean;
  /** GM-only "Persistent as": the staged key, the ledger it reads, and its setter. */
  persistent?: { value: string; wear: Record<string, NpcWear>; onKey: (key: string) => void; onClear?: (key: string) => void };
  onDeploy: () => void;
}) {
  const selectedTemplates = templateOptions.filter((t) => templateIds.includes(t.id));
  const sections = npcOptionalSections({
    npcClass,
    templates: selectedTemplates.map((t) => t.template),
    tier,
    compendium,
  });

  return (
    <div className="cw-npc-config">
      {templateOptions.length > 0 && (
        <div className="cw-tmpl-section">
          <span className="cw-tmpl-label">Templates</span>
          <ul className="cw-tmpl-list">
            {templateOptions.map((t) => {
              const on = templateIds.includes(t.id);
              const reason = templateProhibitReason(t, selectedTemplates);
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    // `cw-tmpl-warn` is a purely dimension-stable state (strikethrough + dashed border +
                    // muted — no inserted glyph), so a discouraged combo NEVER reflows the chip row
                    // The reason stays in the `title` tooltip; the combo
                    // is still fully clickable, never `disabled`.
                    className={`cw-tmpl${on ? " cw-tmpl-on" : ""}${reason ? " cw-tmpl-warn" : ""}`}
                    title={reason ?? `Apply ${t.name}`}
                    disabled={disabled}
                    onClick={() => onToggleTemplate(t.id)}
                  >
                    {t.name}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {sections.map((s) => {
        const count = countPicksIn(s.pool, optionalFeatureIds);
        const outOfBudget = count < s.budget.min || count > s.budget.max;
        return (
          <div key={s.sourceId} className="cw-opt-section">
            <div className="cw-opt-head">
              <span className="cw-opt-label">{s.sourceName} optional features</span>
              <span className={`cw-opt-budget${outOfBudget ? " cw-opt-budget-warn" : ""}`}>
                {count}/{s.budget.min === s.budget.max ? s.budget.max : `${s.budget.min}–${s.budget.max}`}
              </span>
            </div>
            {s.pool.length === 0 ? (
              <div className="cw-hint">no optional features</div>
            ) : (
              <ul className="cw-opt-list">
                {s.pool.map((f) => {
                  const id = f.id;
                  if (id === undefined) return null;
                  const checked = optionalFeatureIds.includes(id);
                  return (
                    <li key={id}>
                      <label className="cw-opt">
                        <input type="checkbox" checked={checked} disabled={disabled} onChange={() => onToggleOptional(id)} />
                        <span>{f.name ?? id}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}

      {persistent && <PersistentKeyField {...persistent} disabled={disabled} />}

      <button type="button" className="cw-deploy-confirm" disabled={disabled} onClick={onDeploy}>
        Deploy
      </button>
    </div>
  );
}

/** One keyed NPC's carried wear as a short line: `HP 27 · Stress 1`, `DESTROYED`, or `pristine`. */
function npcWearSummary(wear: NpcWear | undefined): string {
  if (!wear) return "pristine";
  if (wear.destroyed) return "DESTROYED";
  const parts = [
    ...(wear.hp !== undefined ? [`HP ${wear.hp}`] : []),
    ...(wear.structure !== undefined ? [`Structure ${wear.structure}`] : []),
    ...(wear.stress !== undefined ? [`Stress ${wear.stress}`] : []),
  ];
  return parts.length > 0 ? parts.join(" · ") : "pristine";
}

/**
 * The NPC config's **Persistent as** field (`decisions/0196`): a keyed NPC spawns from the key's
 * carried wear and END COMBAT writes its wear back. Shows what the typed key carries now; the
 * existing keys are offered as a datalist.
 */
function PersistentKeyField({
  value,
  wear,
  onKey,
  onClear,
  disabled,
}: {
  value: string;
  wear: Record<string, NpcWear>;
  onKey: (key: string) => void;
  onClear?: (key: string) => void;
  disabled: boolean;
}) {
  const key = value.trim();
  const known = Object.keys(wear);
  return (
    <div className="cw-opt-section cw-persistent">
      <label className="cw-opt-head">
        <span className="cw-opt-label">Persistent as</span>
        <input
          type="text"
          className="cw-persistent-input"
          aria-label="persistent as"
          placeholder="key (blank = one fight only)"
          list="cw-persistent-keys"
          value={value}
          disabled={disabled}
          onChange={(e) => onKey(e.target.value)}
        />
      </label>
      <datalist id="cw-persistent-keys">
        {known.map((k) => (
          <option key={k} value={k} />
        ))}
      </datalist>
      {key !== "" && (
        <div className="cw-hint" aria-label="persistent wear">
          {key}: {key in wear ? npcWearSummary(wear[key]) : "new key, spawns pristine"} — END COMBAT writes its wear back
          {onClear && key in wear && (
            <button type="button" className="cw-chip" disabled={disabled} title={`Clear ${key}'s carried wear: it spawns pristine`} onClick={() => onClear(key)}>
              CLEAR
            </button>
          )}
        </div>
      )}
    </div>
  );
}
